import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import chatAssistantApi from "app/api/chatAssistant";
import resetChatAssistantQuestion from "app/api/chatAssistant/resetChatAssistantQuestion.api";

// Async thunk for getting chat history
export const getChatHistory = createAsyncThunk(
  "chatAssistant/getChatHistory",
  async ({ userId, language, welcomeMessage }, { rejectWithValue }) => {
    try {
      const response = await chatAssistantApi.getChatAssistantHistory.post({
        userId,
        language,
      });
      return { ...response, welcomeMessage };
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to load chat history");
    }
  }
);

// Async thunk for getting question suggestions
export const getChatQuestionSuggestions = createAsyncThunk(
  "chatAssistant/getChatQuestionSuggestions",
  async ({ userId, language }, { rejectWithValue }) => {
    try {
      const response = await chatAssistantApi.getChatAssistantQuestion.post({
        userId,
        language,
      });
      return response;
    } catch (error) {
      return rejectWithValue(
        error?.message || "Failed to load question suggestions"
      );
    }
  }
);

// Async thunk for sending new question
export const sendNewQuestion = createAsyncThunk(
  "chatAssistant/sendNewQuestion",
  async ({ question, userId }, { rejectWithValue }) => {
    try {
      const response = await chatAssistantApi.chatAssistantNewQuestion.post({
        question,
        userId,
      });
      return {
        question,
        response:
          response?.answer ||
          response?.response ||
          response?.message ||
          response?.data?.answer ||
          "Sorry, I couldn't process your request. Please try again.",
        image_link: response?.image_link || null,
      };
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to send message");
    }
  }
);

// Async thunk for resetting chat session
export const resetChatSession = createAsyncThunk(
  "chatAssistant/resetChatSession",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await resetChatAssistantQuestion.post({ userId });
      return response;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to reset chat session");
    }
  }
);

const initialState = {
  isOpen: false,
  input: "",
  loading: false,
  error: null,
  userId: 1,
  historyLoaded: false,
  hasAskedQuestionInSession: false,
  messages: [],
  exampleQuestions: [
    "How can I reset my password?",
    "Where can I check my order status?",
    "How do I contact customer support?",
  ],
};

const chatAssistantSlice = createSlice({
  name: "chatAssistant",
  initialState,
  reducers: {
    setIsOpen: (state, action) => {
      state.isOpen = action.payload;
      if (!action.payload) {
        state.hasAskedQuestionInSession = false;
      }
    },
    setInput: (state, action) => {
      state.input = action.payload;
    },
    addMessage: (state, action) => {
      state.messages.push(action.payload);
    },
    setMessages: (state, action) => {
      state.messages = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearMessages: (state) => {
      state.messages = [];
      sessionStorage.removeItem("chatAssistantMessages");
      state.historyLoaded = false;
      state.hasAskedQuestionInSession = false;
    },
    setUserId: (state, action) => {
      state.userId = action.payload;
    },
    setHistoryLoaded: (state, action) => {
      state.historyLoaded = action.payload;
    },
    resetHistoryOnRefresh: (state) => {
      state.historyLoaded = false;
    },
    setExampleQuestions: (state, action) => {
      state.exampleQuestions = action.payload;
      console.log("Updated example questions:", action.payload);
    },
    setHasAskedQuestionInSession: (state, action) => {
      state.hasAskedQuestionInSession = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getChatHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getChatHistory.fulfilled, (state, action) => {
        state.loading = false;
        const {
          chat_history = [],
          suggestions = [],
          welcomeMessage,
        } = action.payload || {};

        if (chat_history && chat_history.length > 0) {
          // Convert chat history to our message format
          const formattedMessages = chat_history.map((msg) => ({
            role: msg.sender_type === "user" ? "user" : "assistant",
            content: msg.text,
            image_link: msg.image_link,
            id: msg.id,
            created_at: msg.created_at,
          }));
          // Store messages in Redux state
          state.messages = formattedMessages;
          // Also backup to sessionStorage
          sessionStorage.setItem(
            "chatAssistantMessages",
            JSON.stringify(formattedMessages)
          );
        } else {
          // No chat history, show welcome message from container
          const welcomeMsg = [
            {
              role: "assistant",
              content:
                welcomeMessage ||
                "Hello! Feel free to message me anything. I'm here to help you with your questions.",
            },
          ];
          // Store welcome message in Redux state
          state.messages = welcomeMsg;
          // Also backup to sessionStorage
          sessionStorage.setItem(
            "chatAssistantMessages",
            JSON.stringify(welcomeMsg)
          );
        }

        // Update example questions from suggestions
        if (suggestions && suggestions.length > 0) {
          state.exampleQuestions = suggestions.map((s) => s.question);
        }

        state.historyLoaded = true;
      })
      .addCase(getChatHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        // Show welcome message on error in sessionStorage
        const welcomeMessage = [
          {
            role: "assistant",
            content:
              action.meta.arg.welcomeMessage ||
              "Hello! Feel free to message me anything. I'm here to help you with your questions.",
          },
        ];
        sessionStorage.setItem(
          "chatAssistantMessages",
          JSON.stringify(welcomeMessage)
        );
        state.historyLoaded = true;
      })
      // Send New Question reducers
      .addCase(sendNewQuestion.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        // Mark that user has asked a question in this session
        state.hasAskedQuestionInSession = true;
        // Add user message immediately to Redux state
        const userMessage = {
          role: "user",
          content: action.meta.arg.question,
        };
        state.messages.push(userMessage);
        // Also backup to sessionStorage
        const existingMessages = JSON.parse(
          sessionStorage.getItem("chatAssistantMessages") || "[]"
        );
        existingMessages.push(userMessage);
        sessionStorage.setItem(
          "chatAssistantMessages",
          JSON.stringify(existingMessages)
        );
      })
      .addCase(sendNewQuestion.fulfilled, (state, action) => {
        state.loading = false;
        // Add assistant response to Redux state
        const assistantMessage = {
          role: "assistant",
          content: action.payload.response,
          image_link: action.payload.image_link,
        };
        state.messages.push(assistantMessage);
        // Also backup to sessionStorage
        const existingMessages = JSON.parse(
          sessionStorage.getItem("chatAssistantMessages") || "[]"
        );
        existingMessages.push(assistantMessage);
        sessionStorage.setItem(
          "chatAssistantMessages",
          JSON.stringify(existingMessages)
        );
      })
      .addCase(sendNewQuestion.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        // Add error message to sessionStorage
        const existingMessages = JSON.parse(
          sessionStorage.getItem("chatAssistantMessages") || "[]"
        );
        existingMessages.push({
          role: "assistant",
          content: "Sorry, I encountered an error. Please try again later.",
        });
        sessionStorage.setItem(
          "chatAssistantMessages",
          JSON.stringify(existingMessages)
        );
      })
      // Get Chat Question Suggestions reducers
      .addCase(getChatQuestionSuggestions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getChatQuestionSuggestions.fulfilled, (state, action) => {
        state.loading = false;
        const { suggestions = [] } = action.payload || {};

        // Only update suggestions, don't touch messages/history
        if (suggestions && suggestions.length > 0) {
          state.exampleQuestions = suggestions.map((s) => s.question);
        }
      })
      .addCase(getChatQuestionSuggestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Reset chat session cases
      .addCase(resetChatSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetChatSession.fulfilled, (state, action) => {
        state.loading = false;
        // Clear messages from Redux state and session storage
        state.messages = [];
        sessionStorage.removeItem("chatAssistantMessages");
        state.historyLoaded = false;
        state.hasAskedQuestionInSession = false;
      })
      .addCase(resetChatSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setIsOpen,
  setInput,
  addMessage,
  setMessages,
  setLoading,
  setError,
  clearMessages,
  setUserId,
  setHistoryLoaded,
  resetHistoryOnRefresh,
  setExampleQuestions,
  setHasAskedQuestionInSession,
} = chatAssistantSlice.actions;

export const chatAssistantReducer = chatAssistantSlice.reducer;

// Utility function to get messages from sessionStorage
export const getMessagesFromSession = () => {
  try {
    const storedMessages = sessionStorage.getItem("chatAssistantMessages");
    return storedMessages ? JSON.parse(storedMessages) : [];
  } catch (error) {
    console.error("Error parsing chat messages from sessionStorage:", error);
    return [];
  }
};

// Utility function to clear messages from sessionStorage
export const clearMessagesFromSession = () => {
  sessionStorage.removeItem("chatAssistantMessages");
};
